import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faBars, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragStartEvent,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import "./css/certificate-manage.css";

// 정렬 가능한 자격증 아이템 컴포넌트
function SortableCertificateItem({ cert, index, onToggleVisibility }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: cert.id });

    // 수직 이동만 허용 (x축은 0으로 고정)
    const style = {
        transform: transform ? CSS.Transform.toString({
            ...transform,
            x: 0, // 수평 이동 제한
        }) : undefined,
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="certificate-item"
        >
            <div className="certificate-item-info">
                <div className="certificate-item-number">{index + 1}</div>
                <div className="certificate-item-details">
                    <span className="certificate-item-name">{cert.name || "자격증"}</span>
                    <span className="certificate-item-status">
                        {cert.status === "approved" ? "승인완료" : "대기중"}
                    </span>
                </div>
            </div>
            <div className="certificate-item-actions">
                <button
                    className="certificate-toggle-btn"
                    onClick={() => onToggleVisibility(cert.id)}
                    title={cert.visible ? "숨기기" : "보이기"}
                >
                    <FontAwesomeIcon icon={cert.visible ? faEye : faEyeSlash} />
                </button>
                <button
                    className="certificate-hamburger-btn"
                    {...attributes}
                    {...listeners}
                    title="드래그하여 순서 변경"
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <FontAwesomeIcon icon={faBars} />
                </button>
            </div>
        </div>
    );
}

export default function CertificateManage() {
    const navigate = useNavigate();
    
    // 기본 모바일 신분증 상태
    const [defaultCertVisible, setDefaultCertVisible] = useState(true);
    
    // 자격증 목록 상태
    const [certificates, setCertificates] = useState([]);
    
    // 자격증 로드
    useEffect(() => {
        loadCertificates();
    }, []);
    
    const loadCertificates = () => {
        // localStorage에서 자격증 데이터 로드
        const pendingCertData = localStorage.getItem("hb_pending_cert");
        const pendingCertsData = localStorage.getItem("hb_pending_certs");
        
        const certs = [];
        
        // 단일 pending cert
        if (pendingCertData) {
            try {
                const cert = JSON.parse(pendingCertData);
                certs.push({
                    ...cert,
                    id: 'pending_single',
                    type: 'pending',
                    visible: cert.visible !== undefined ? cert.visible : true,
                    order: cert.order !== undefined ? cert.order : 0
                });
            } catch (e) {
                console.error("Failed to parse pending cert:", e);
            }
        }
        
        // 여러 pending certs
        if (pendingCertsData) {
            try {
                const certsArray = JSON.parse(pendingCertsData);
                certsArray.forEach((cert, index) => {
                    if (cert.status === "approved" || cert.status === "pending") {
                        certs.push({
                            ...cert,
                            id: `pending_${index}`,
                            type: cert.status,
                            visible: cert.visible !== undefined ? cert.visible : true,
                            order: cert.order !== undefined ? cert.order : index
                        });
                    }
                });
            } catch (e) {
                console.error("Failed to parse pending certs:", e);
            }
        }
        
        // order 기준으로 정렬
        certs.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setCertificates(certs);
        
        // localStorage에서 기본 자격증 표시 상태 확인
        const savedVisible = localStorage.getItem("hb_default_cert_visible");
        if (savedVisible !== null) {
            setDefaultCertVisible(savedVisible === "true");
        } else {
            // 자격증이 한 개라도 있으면 기본 자격증 숨김 (기본값)
            if (certs.length > 0) {
                setDefaultCertVisible(false);
            }
        }
    };
    
    // 자격증 저장
    const saveCertificates = (updatedCerts) => {
        // 단일 cert와 여러 certs를 구분하여 저장
        const singleCert = updatedCerts.find(cert => cert.id === 'pending_single');
        const multipleCerts = updatedCerts.filter(cert => cert.id !== 'pending_single');
        
        if (singleCert) {
            localStorage.setItem("hb_pending_cert", JSON.stringify(singleCert));
        } else {
            localStorage.removeItem("hb_pending_cert");
        }
        
        if (multipleCerts.length > 0) {
            localStorage.setItem("hb_pending_certs", JSON.stringify(multipleCerts));
        } else {
            localStorage.removeItem("hb_pending_certs");
        }
    };
    
    // 기본 자격증 표시/숨김 토글
    const toggleDefaultCert = () => {
        // 기본 자격증을 숨기려고 할 때, 다른 자격증이 모두 숨김인지 확인
        if (defaultCertVisible) {
            const visibleCertsCount = certificates.filter(cert => cert.visible !== false).length;
            if (visibleCertsCount === 0) {
                alert("최소 한 개는 노출되어야 합니다.");
                return;
            }
        }
        const newVisible = !defaultCertVisible;
        setDefaultCertVisible(newVisible);
        // localStorage에 저장
        localStorage.setItem("hb_default_cert_visible", String(newVisible));
    };
    
    // 자격증 표시/숨김 토글
    const toggleCertVisibility = (certId) => {
        const certToToggle = certificates.find(cert => cert.id === certId);
        if (!certToToggle) return;
        
        // 숨기려고 할 때, 다른 자격증이 모두 숨김인지 확인
        if (certToToggle.visible !== false) {
            const otherVisibleCertsCount = certificates.filter(cert => 
                cert.id !== certId && cert.visible !== false
            ).length;
            const isDefaultVisible = defaultCertVisible;
            
            if (otherVisibleCertsCount === 0 && !isDefaultVisible) {
                alert("최소 한 개는 노출되어야 합니다.");
                return;
            }
        }
        
        const updatedCerts = certificates.map(cert => {
            if (cert.id === certId) {
                return { ...cert, visible: !cert.visible };
            }
            return cert;
        });
        setCertificates(updatedCerts);
        saveCertificates(updatedCerts);
    };
    
    // 드래그 앤 드롭 센서 설정 (수직 이동만 허용)
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // 8px 이상 이동해야 드래그 시작
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );
    
    // 드래그 앤 드롭 핸들러
    const handleDragEnd = (event) => {
        const { active, over } = event;
        
        if (over && active.id !== over.id) {
            setCertificates((items) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);
                
                const newItems = arrayMove(items, oldIndex, newIndex);
                // order 업데이트
                newItems.forEach((cert, idx) => {
                    cert.order = idx;
                });
                saveCertificates(newItems);
                return newItems;
            });
        }
    };
    
    // localStorage에서 기본 자격증 표시 상태 로드
    useEffect(() => {
        const savedVisible = localStorage.getItem("hb_default_cert_visible");
        if (savedVisible !== null) {
            setDefaultCertVisible(savedVisible === "true");
        }
    }, []);
    
    return (
        <div className="certificate-manage-wrap">
            {/* 헤더 */}
            <div className="certificate-manage-header">
                <button
                    className="certificate-manage-back-btn"
                    onClick={() => navigate("/setting")}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <h1 className="certificate-manage-title">자격증관리</h1>
                <div style={{ width: "40px" }} /> {/* 중앙 정렬을 위한 공간 */}
            </div>
            
            {/* 본문 */}
            <div className="certificate-manage-content">
                {/* 기본 모바일 신분증 섹션 */}
                <div className="certificate-section">
                    <div className="certificate-section-title">기본 모바일 신분증</div>
                    <div className="certificate-item">
                        <div className="certificate-item-info">
                            <span className="certificate-item-name">모바일 신분증</span>
                        </div>
                        <div className="certificate-item-actions">
                            <button
                                className="certificate-toggle-btn"
                                onClick={toggleDefaultCert}
                                title={defaultCertVisible ? "숨기기" : "보이기"}
                            >
                                <FontAwesomeIcon icon={defaultCertVisible ? faEye : faEyeSlash} />
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* 신청한 자격증 섹션 */}
                <div className="certificate-section certificate-certificates-section">
                    <div className="certificate-section-title">신청한 자격증</div>
                    <div className="certificate-list-container">
                        {certificates.length === 0 ? (
                            <div className="certificate-empty">신청한 자격증이 없습니다.</div>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={certificates.map(cert => cert.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {certificates.map((cert, index) => (
                                        <SortableCertificateItem
                                            key={cert.id}
                                            cert={cert}
                                            index={index}
                                            onToggleVisibility={toggleCertVisibility}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

